/**
 * IntegrationsHome — Public integrations catalog (integrations.example.com)
 * Grid of integration cards with category filters, status badges, search
 * MIGRADO: Badges custom → Badge ui/ | SkeletonCard → SkeletonBar ui/
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';

import { T } from '../styles/tokens';
import { SearchInput, Button, Badge, SkeletonBar } from '../components/ui';
const API_BASE = '/api/v1/cms';

interface IntegrationItem {
  id: string; slug: string; metadata: string; status: string;
  is_popular: number; view_count: number; tags: string;
  published_at: string; title: string; excerpt: string; features_json: string; custom_fields: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
  // Computed from metadata
  icon_url?: string; website_url?: string; name?: string; short_description?: string; min_plan?: string;
}
interface Category { id: string; slug: string; icon: string; color: string; name: string; description: string; item_count: number; }

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return navigator.language?.startsWith('pt') ? 'pt-BR' : 'en';
}

const statusLabels: Record<string, { label: string; color: string; variant: 'success' | 'warning' | 'info' | 'error' | 'neutral' }> = {
  available: { label: 'Disponivel', color: T.success, variant: 'success' },
  coming_soon: { label: 'Em breve', color: T.warning, variant: 'warning' },
  beta: { label: 'Beta', color: T.primary, variant: 'info' },
};

const categoryIcons: Record<string, string> = {
  'message-circle': 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  'git-branch': 'M6 3v12M18 9a3 3 0 01-3 3h-3M6 15a3 3 0 003 3h3a3 3 0 003-3V9',
  'layout': 'M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zM3 9h18M9 21V9',
  'credit-card': 'M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2zM1 10h22',
  'server': 'M2 4h20v6H2zM2 14h20v6H2zM6 7h.01M6 17h.01',
};

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  hero: { textAlign: 'center' as const, padding: '60px 20px 40px', marginBottom: 32 } as any,
  heroTitle: { fontSize: 42, fontWeight: 800, lineHeight: 1.1, margin: '0 0 16px', background: `linear-gradient(135deg, ${T.text}, ${T.purple})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  heroSub: { fontSize: 18, color: T.textSecondary, maxWidth: 600, margin: '0 auto', lineHeight: 1.6 } as any,
  searchWrap: { maxWidth: 500, margin: '32px auto 0', position: 'relative' as const } as any,
  filterBar: { display: 'flex', gap: 10, flexWrap: 'wrap' as const, justifyContent: 'center', marginBottom: 32 } as any,
  filterPill: (active: boolean, color: string) => ({
    padding: '8px 18px', borderRadius: 24, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? `${color}20` : T.bgCardHover,
    color: active ? color : T.textSecondary,
    transition: 'all 0.15s',
  }),
  statusFilter: { display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 32 } as any,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginBottom: 60 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: '28px 24px', cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s', position: 'relative' as const, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', textAlign: 'center' as const } as any,
  cardIcon: { width: 56, height: 56, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 16, background: T.bgCardHover } as any,
  cardName: { fontSize: 17, fontWeight: 700, margin: '0 0 8px', color: T.text } as any,
  cardDesc: { fontSize: 13, color: T.textSecondary, lineHeight: 1.5, margin: '0 0 16px', minHeight: 40 } as any,
  cardBadges: { display: 'flex', gap: 8, flexWrap: 'wrap' as const, justifyContent: 'center' } as any,
  popularStar: { position: 'absolute' as const, top: 12, right: 12, color: T.warning, fontSize: 16 } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 } as any,
  empty: { textAlign: 'center' as const, padding: '60px 20px', color: T.textMuted } as any,
};

function IntegrationCard({ item, locale }: { item: IntegrationItem; locale: string }) {
  const st = statusLabels[item.status] || statusLabels.available;
  return (
    <div style={S.card} onClick={() => { window.location.href = `/${locale}/${item.slug}`; }}
      onMouseOver={(e: any) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.borderColor = `${item.category_color || T.primary}40`; e.currentTarget.style.boxShadow = `0 0 24px ${item.category_color || T.primary}15`; }}
      onMouseOut={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}>
      {item.is_popular === 1 && <span style={S.popularStar} title="Popular">&#9733;</span>}
      <div style={{ ...S.cardIcon, background: `${item.category_color || T.primary}15` }}>
        {item.icon_url ? <img src={item.icon_url} alt="" style={{ width: 32, height: 32 }} /> :
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={item.category_color || T.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={categoryIcons[item.category_icon] || categoryIcons['layout']} />
          </svg>}
      </div>
      <h3 style={S.cardName}>{item.name || item.slug}</h3>
      <p style={S.cardDesc}>{item.short_description || ''}</p>
      <div style={S.cardBadges}>
        {/* MIGRADO: Badge inline custom → Badge ui/ */}
        <Badge variant="info" color={item.category_color || T.primary}>
          {item.category_name || item.category_slug}
        </Badge>
        <Badge variant={st.variant} color={st.color}>
          {st.label}
        </Badge>
        {item.min_plan !== 'free' && (
          <Badge variant="neutral" color={T.purple}>
            {item.min_plan.toUpperCase()}
          </Badge>
        )}
      </div>
    </div>
  );
}

// MIGRADO: SkeletonCard custom → SkeletonBar ui/
function SkeletonCard() {
  return (
    <div style={{ ...S.card, minHeight: 240, padding: 20 }}>
      <SkeletonBar width={56} height={56} style={{ borderRadius: 14, marginBottom: 16 }} />
      <SkeletonBar width="70%" height={16} style={{ marginBottom: 8 }} />
      <SkeletonBar width="90%" height={12} style={{ marginBottom: 4 }} />
      <SkeletonBar width="80%" height={12} style={{ marginBottom: 16 }} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <SkeletonBar width={70} height={20} style={{ borderRadius: 20 }} />
        <SkeletonBar width={70} height={20} style={{ borderRadius: 20 }} />
      </div>
    </div>
  );
}

export function IntegrationsHome() {
  const locale = getLocale();
  const [items, setItems] = useState<IntegrationItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/categories?contentType=integrations&locale=${locale}`)
      .then(r => r.json())
      .then(d => setCategories(d.data?.categories || []))
      .catch(() => {});
  }, [locale]);

  useEffect(() => {
    setLoading(true);
    let url = searchQuery.length >= 2
      ? `${API_BASE}/search?contentType=integrations&q=${encodeURIComponent(searchQuery)}&locale=${locale}`
      : `${API_BASE}/entries?contentType=integrations&status=published&locale=${locale}${activeCategory ? `&category=${activeCategory}` : ''}`;
    fetch(url)
      .then(r => r.json())
      .then(d => {
        const entries = d.data?.entries || [];
        // Map CMS fields to integration shape
        const mapped = entries.map((e: any) => {
          const meta: any = (() => { try { return JSON.parse(e.metadata || '{}'); } catch { return {}; } })();
          return { ...e, name: e.title, short_description: e.excerpt, icon_url: meta.icon_url || '', website_url: meta.website_url || '', min_plan: meta.min_plan || 'free', status: meta.status || 'available' };
        });
        setItems(mapped);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [locale, activeCategory, activeStatus, searchQuery]);

  return (
    <div style={S.page}>
      <div style={S.container}>
        <header style={S.header}>
          <a style={S.logo} href="/">
            <span style={S.logoAccent}>WhiteLabelClaw</span>
            <span style={{ color: T.textMuted, fontWeight: 400 }}>/ Integracoes</span>
          </a>
          <nav style={{ display: 'flex', gap: 16 }}>
            <a href="window.location.origin" style={{ color: T.textSecondary, textDecoration: 'none', fontSize: 14 }}>Plataforma</a>
            <a href="/developers" style={{ color: T.textSecondary, textDecoration: 'none', fontSize: 14 }}>Developers</a>
          </nav>
        </header>

        <div style={S.hero}>
          <h1 style={S.heroTitle}>Integracoes</h1>
          <p style={S.heroSub}>Conecte a WhiteLabelClaw com suas ferramentas favoritas. Automatize fluxos, receba notificacoes e expanda suas possibilidades.</p>
          <div style={S.searchWrap}>
            <SearchInput placeholder="Buscar integracoes..." onSearch={setSearchQuery} debounceMs={300} />
          </div>
        </div>

        <div style={S.filterBar}>
          <Button variant="ghost" size="sm" style={S.filterPill(!activeCategory, T.primary)} onClick={() => setActiveCategory('')}>Todas</Button>
          {categories.map(cat => (
            <Button variant="ghost" size="sm" key={cat.slug} style={S.filterPill(activeCategory === cat.slug, cat.color)}
              onClick={() => setActiveCategory(activeCategory === cat.slug ? '' : cat.slug)}>
              {cat.name || cat.slug} ({cat.item_count})
            </Button>
          ))}
        </div>

        <div style={S.statusFilter}>
          <Button variant="ghost" size="sm" style={S.filterPill(!activeStatus, T.primary)} onClick={() => setActiveStatus('')}>Todas</Button>
          <Button variant="ghost" size="sm" style={S.filterPill(activeStatus === 'available', T.success)} onClick={() => setActiveStatus(activeStatus === 'available' ? '' : 'available')}>Disponivel</Button>
          <Button variant="ghost" size="sm" style={S.filterPill(activeStatus === 'coming_soon', T.warning)} onClick={() => setActiveStatus(activeStatus === 'coming_soon' ? '' : 'coming_soon')}>Em breve</Button>
        </div>

        {loading ? (
          <div style={S.grid}>
            {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <div style={S.empty}>
            <p style={{ fontSize: 18, fontWeight: 600 }}>Nenhuma integracao encontrada</p>
            <p>Tente ajustar os filtros ou busca.</p>
          </div>
        ) : (
          <div style={S.grid}>
            {items.map(item => <IntegrationCard key={item.id} item={item} locale={locale} />)}
          </div>
        )}

        <footer style={S.footer}>
          <p>&copy; {new Date().getFullYear()} WhiteLabelClaw. Todas as integracoes.</p>
        </footer>
      </div>
    </div>
  );
}
